import { LOGO_MAX_BYTES } from '@kratispot/shared';

// A logo is a tile on a card, so 512 on its longest side is more than enough.
export const LOGO_MAX_SIDE = 512;

export const LOGO_TYPE = 'image/webp';

// WebKit hands back PNG for a WebP ask, and a photograph then blows the cap. §3
export const LOGO_FALLBACK_TYPE = 'image/jpeg';

export const LOGO_FALLBACK_QUALITY = 0.85;

export type Size = { readonly width: number; readonly height: number };

export function fitWithin(width: number, height: number, max = LOGO_MAX_SIDE): Size {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: sideOf(width, scale), height: sideOf(height, scale) };
}

const sideOf = (side: number, scale: number) => Math.max(1, Math.round(side * scale));

// A blob the server would refuse: the wrong type, or past the shared cap. §3
const refused = (blob: Blob) => blob.type !== LOGO_TYPE || blob.size > LOGO_MAX_BYTES;

// The browser downsizes, so the wire carries a tile and never a photograph. §3
export async function downsized(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const first = await drawn(bitmap, fitWithin(bitmap.width, bitmap.height), LOGO_TYPE);
  if (!refused(first)) return first;
  const size = fitWithin(bitmap.width, bitmap.height);
  const jpeg = await drawn(bitmap, size, LOGO_FALLBACK_TYPE, LOGO_FALLBACK_QUALITY);
  if (jpeg.size <= LOGO_MAX_BYTES) return jpeg;
  const half = fitWithin(bitmap.width, bitmap.height, LOGO_MAX_SIDE / 2);
  const smaller = await drawn(bitmap, half, LOGO_FALLBACK_TYPE, LOGO_FALLBACK_QUALITY);
  if (smaller.size <= LOGO_MAX_BYTES) return smaller;
  throw new Error('cannot encode a logo');
}

async function drawn(
  bitmap: ImageBitmap,
  size: Size,
  type: string,
  quality?: number,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('this browser cannot resize an image');
  context.drawImage(bitmap, 0, 0, size.width, size.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob === null ? reject(new Error('cannot encode a logo')) : resolve(blob)),
      type,
      quality,
    );
  });
}
